import React, { useState } from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";
import ThemedChartTooltip, { ThemedTooltipBox } from "../../shared/ThemedChartTooltip";
import InfoTip from "../../shared/InfoTip";
import { fmtCurrency } from "../../shared/format.js";

/**
 * MetricTrendChart — metric_history evidence component.
 *
 * Multi-line chart over date, with a series toggle.
 * Values are as they stood on each snapshot day — not recalculated.
 * Under 7 points the panel arrives insufficient (handled by PanelCard).
 * Between 7 and 30 points, caption "Early trend — {n} days".
 *
 * Spec references: §7 (metric_history).
 */

const SERIES = [
  { key: "performance_pct", label: "Performance %", color: "#3B82F6" },
  { key: "weather_effect_bookings", label: "Weather effect (bookings)", color: "#F97316" },
  { key: "revenue_lost", label: "Revenue lost ($)", color: "#EF4444" },
  { key: "comfort_percentile", label: "Comfort percentile", color: "#22C55E" },
  { key: "escape_pressure_f", label: "Escape pressure (°F)", color: "#8B5CF6" },
];

const fmtDate = (d) => {
  if (!d) return "";
  const dt = new Date(d + "T00:00:00");
  return dt.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

const MetricTrendChart = ({ panel }) => {
  const f = panel.facts || {};
  const points = f.points || [];

  // Under 7 points — insufficient is handled by PanelCard,
  // but if somehow we get here with fewer, don't render a misleading chart
  if (points.length < 2) return null;

  // Determine which series have data
  const activeSeries = SERIES.filter((s) =>
    points.some((p) => p[s.key] != null)
  );

  const [enabled, setEnabled] = useState(() =>
    new Set(activeSeries.map((s) => s.key))
  );

  const toggle = (key) => {
    setEnabled((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    <div className="space-y-4">
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow border border-gray-200 dark:border-gray-800 p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center">
            <span>How These Numbers Have Moved</span>
            <InfoTip text="Daily snapshot of key metrics over time. Toggle series on or off to compare. Values are as they stood on each day — not recalculated — which is what makes the trend honest." />
          </h3>
        </div>

        {/* Series toggle */}
        <div className="flex flex-wrap gap-1.5 mb-3">
          {activeSeries.map((s) => {
            const on = enabled.has(s.key);
            return (
              <button
                key={s.key}
                type="button"
                onClick={() => toggle(s.key)}
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium border transition-colors ${
                  on
                    ? "border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300"
                    : "border-transparent bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500"
                }`}
              >
                <span
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: on ? s.color : "#D1D5DB" }}
                />
                {s.label}
              </button>
            );
          })}
        </div>

        <ResponsiveContainer width="100%" height={220}>
          <LineChart
            data={points}
            margin={{ top: 5, right: 10, left: 0, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#374151" vertical={false} />
            <XAxis
              dataKey="date"
              tickFormatter={fmtDate}
              tick={{ fontSize: 10, fill: "#9ca3af" }}
              axisLine={{ stroke: "#374151" }}
              tickLine={false}
            />
            <YAxis
              tick={{ fontSize: 10, fill: "#9ca3af" }}
              axisLine={false}
              tickLine={false}
              width={45}
            />
            <ThemedChartTooltip
              content={({ active, payload, label }) => {
                if (!active || !payload?.length) return null;
                return (
                  <ThemedTooltipBox title={fmtDate(label)}>
                    {payload.map((p, i) => (
                      <div key={i} style={{ color: p.color }}>
                        {p.name}:{" "}
                        {p.dataKey === "revenue_lost" && typeof p.value === "number"
                          ? fmtCurrency(p.value)
                          : typeof p.value === "number"
                            ? p.value.toFixed(1)
                            : p.value}
                      </div>
                    ))}
                  </ThemedTooltipBox>
                );
              }}
            />
            {activeSeries.map((s) =>
              enabled.has(s.key) ? (
                <Line
                  key={s.key}
                  type="monotone"
                  dataKey={s.key}
                  name={s.label}
                  stroke={s.color}
                  strokeWidth={2}
                  dot={{ r: 3, fill: s.color }}
                  activeDot={{ r: 5 }}
                  connectNulls
                />
              ) : null
            )}
          </LineChart>
        </ResponsiveContainer>
        {f.means && (
          <p className="text-[11px] text-gray-400 dark:text-gray-500 text-center italic mt-3">
            {f.means}
          </p>
        )}
      </div>
    </div>
  );
};

export default MetricTrendChart;
